import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useAppDispatch } from "../../../redux/store"
import {
  changeContact,
  getContactsOnEvent,
  setLoading,
} from "../../../redux/slices/contacts/contactsSlices"
import { updateInvite } from "../../../redux/slices/events/eventsSlice"
import CreatableSelect from "react-select/creatable"
import PhoneInput, { CountryData } from "react-phone-input-2"
import "react-phone-input-2/lib/style.css"
import {
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js"
import Drawer from "./Drawer"
import styles from "../EventDashboardV2.module.scss"
import { buildSuffixOptions } from "../constants/suffixOptions"

const selectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    minHeight: 38,
    fontSize: 14,
    borderColor: state.isFocused ? "#a0a8b8" : "#e5e7eb",
    borderRadius: 6,
    boxShadow: state.isFocused ? "0 0 0 3px rgba(0,0,0,0.04)" : "none",
    "&:hover": { borderColor: "#a0a8b8" },
  }),
  valueContainer: (base: any) => ({ ...base, padding: "2px 12px" }),
  indicatorSeparator: () => ({ display: "none" }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? "#f3f4f6" : state.isFocused ? "#f9fafb" : "white",
    color: "#1a1a1a",
    "&:active": { backgroundColor: "#f3f4f6" },
  }),
  menu: (base: any) => ({ ...base, borderRadius: 6, boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }),
}

const GULF_COUNTRIES = ["sa", "ae", "bh", "kw", "qa", "om"]

const stripPipeSuffix = (phone: string) => {
  if (!phone) return ""
  const idx = phone.indexOf("|")
  return idx >= 0 ? phone.substring(0, idx) : phone
}

const getPipeSuffix = (phone: string) => {
  if (!phone) return ""
  const idx = phone.indexOf("|")
  return idx >= 0 ? phone.substring(idx) : ""
}

const toE164 = (digits: string) => (digits ? "+" + digits : "")

const SENT_STATUSES = [1, 2, 3, 4]

const formatPhoneDisplay = (phone: string): string => {
  if (!phone) return "—"
  const e164 = toE164(phone)
  const parsed = parsePhoneNumberFromString(e164)
  if (parsed) return parsed.formatInternational()
  return "+" + phone
}

const LockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
    <path
      d="M11.5 7H4.5C3.67 7 3 7.67 3 8.5V13.5C3 14.33 3.67 15 4.5 15H11.5C12.33 15 13 14.33 13 13.5V8.5C13 7.67 12.33 7 11.5 7Z"
      stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"
    />
    <path
      d="M5 7V4.5C5 3.70435 5.31607 2.94129 5.87868 2.37868C6.44129 1.81607 7.20435 1.5 8 1.5C8.79565 1.5 9.55871 1.81607 10.1213 2.37868C10.6839 2.94129 11 3.70435 11 4.5V7"
      stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"
    />
  </svg>
)

const EditGuestDrawer = ({
  guest,
  setGuest,
  show,
  setShow,
  validation,
  setValidation,
  filter,
  event_id,
  notify,
}: any) => {
  const { t, i18n } = useTranslation()
  const dispatch = useAppDispatch()
  const suffixOptions = useMemo(() => buildSuffixOptions(t), [t])

  const [saving, setSaving] = useState(false)
  const pipeSuffix = useRef("")
  const prevShow = useRef(false)

  const isContactLocked = SENT_STATUSES.includes(guest.status)

  useEffect(() => {
    if (show && !prevShow.current) {
      const rawPhone = guest.phone || ""
      pipeSuffix.current = getPipeSuffix(rawPhone)
      const cleanPhone = stripPipeSuffix(rawPhone)
      if (rawPhone !== cleanPhone) {
        setGuest({ ...guest, phone: cleanPhone })
      }
      setValidation([])
    }
    prevShow.current = show
  }, [show])

  const handlePhoneChange = (value: string, _data: CountryData | {}) => {
    setGuest({ ...guest, phone: value })
    if (validation?.phone) {
      setValidation((prev: any) => {
        const next = { ...prev }
        delete next.phone
        return next
      })
    }
  }

  const validatePhone = (value: string): string | null => {
    if (isContactLocked) return null
    const e164 = toE164(value)
    if (!value) return t("eventDashboardV2.validation.phoneRequired")

    const lengthResult = validatePhoneNumberLength(e164)
    if (lengthResult === "TOO_SHORT") return t("eventDashboardV2.validation.phoneIncomplete")
    if (lengthResult === "TOO_LONG") return t("eventDashboardV2.validation.phoneTooLong")
    if (lengthResult === "NOT_A_NUMBER") return t("eventDashboardV2.validation.phoneInvalid")

    const parsed = parsePhoneNumberFromString(e164)
    if (!parsed) return t("eventDashboardV2.validation.phoneInvalid")
    if (!parsed.isValid()) {
      const country = parsed.country
      return country
        ? t("eventDashboardV2.validation.phoneInvalidForCountry", { country: new Intl.DisplayNames([i18n.language === "ar" ? "ar" : "en"], { type: "region" }).of(country) })
        : t("eventDashboardV2.validation.phoneInvalid")
    }

    return null
  }

  const handeInput = (field: string, value: any) => {
    setGuest({ ...guest, [field]: value })
    if (validation?.[field]) {
      setValidation((prev: any) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const validate = (): Record<string, string> => {
    const errors: Record<string, string> = {}
    const firstName = String(guest.first_name || "").trim()
    const email = String(guest.email || "").trim()

    if (!firstName) errors.first_name = t("eventDashboardV2.validation.firstNameRequired")
    if (!isContactLocked) {
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
        errors.email = t("eventDashboardV2.validation.emailInvalid")
      const phoneError = validatePhone(String(guest.phone || ""))
      if (phoneError) errors.phone = phoneError
    }
    const qty = Number(guest.quantity_members)
    if (isNaN(qty) || qty < 0 || qty > 10)
      errors.invites_quantity = t("eventDashboardV2.validation.additionalInvitesRange")
    return errors
  }

  const resetAndClose = () => {
    setShow(false)
    setSaving(false)
    setValidation([])
  }

  const saveEditGuest = (event?: { preventDefault: () => void }) => {
    event?.preventDefault()

    const errors = validate()
    if (Object.keys(errors).length > 0) {
      setValidation(errors)
      return
    }

    const cleanPhone = isContactLocked
      ? stripPipeSuffix(guest.phone || "").replace(/\D/g, "") + pipeSuffix.current
      : (guest.phone || "").replace(/\D/g, "") + pipeSuffix.current

    const payload = {
      id: guest.id,
      first_name: String(guest.first_name || "").trim(),
      last_name: String(guest.last_name || "").trim(),
      suffix: guest.suffix || "",
      zone: String(guest.zone ?? "").trim(),
      phone: cleanPhone,
      email: String(guest.email || "").trim(),
    }

    setSaving(true)
    dispatch(changeContact(payload))
      .then((result: any) => {
        if (result.error) {
          if (result.payload && typeof result.payload === "object") {
            setValidation(result.payload)
            const firstError = (Object.values(result.payload) as string[][])[0]?.[0]
            if (firstError) notify(firstError, true)
          } else {
            notify(t("eventDashboardV2.messages.failedSaveContact"), true)
          }
          setSaving(false)
          return
        }

        return dispatch(
          updateInvite({
            event_id: event_id,
            contact_id: guest.id,
            quantity_members: guest.quantity_members,
            zone: payload.zone,
          }),
        ).then((inviteResult: any) => {
          if (inviteResult?.error) {
            const errPayload = inviteResult.payload
            if (errPayload && typeof errPayload === "object") {
              const firstError = (Object.values(errPayload) as string[][])[0]?.[0]
              if (firstError) notify(firstError, true)
            } else {
              notify(t("eventDashboardV2.messages.failedUpdateInvite"), true)
            }
            setSaving(false)
            return
          }

          dispatch(setLoading())
          dispatch(getContactsOnEvent(filter))
          notify(t("eventDashboardV2.messages.changesSaved"), false)
          setSaving(false)
          resetAndClose()
        })
      })
      .catch(() => {
        setSaving(false)
        notify(t("eventDashboardV2.messages.somethingWentWrongTryAgain"), true)
      })
  }

  const guestName = [guest.suffix, guest.first_name, guest.last_name]
    .filter(Boolean)
    .join(" ")

  return (
    <Drawer
      open={show}
      onClose={resetAndClose}
      title={t("eventDashboardV2.messages.editGuest")}
      footer={
        <div className={styles.drawerFooterActions}>
          <button type="button" className={styles.drawerBtnCancel} onClick={resetAndClose}>
            {t("eventDashboardV2.actions.cancel")}
          </button>
          <button
            type="button"
            className={styles.drawerBtnSave}
            disabled={saving}
            onClick={() => saveEditGuest()}
          >
            {saving ? t("eventDashboardV2.messages.saving") : t("eventDashboardV2.actions.saveChanges")}
          </button>
        </div>
      }
    >
      {guestName && (
        <div className={styles.drawerGuestBanner}>
          <div className={styles.drawerGuestAvatar}>
            {(guest.first_name || t("eventDashboardV2.labels.guest")).charAt(0).toUpperCase()}
          </div>
          <div>
            <p className={styles.drawerGuestName}>{guestName}</p>
            <p className={styles.drawerGuestId}>
              {t("eventDashboardV2.messages.drawerGuestIdLabel", { id: guest.id })}
            </p>
          </div>
        </div>
      )}

      <form id="edit-guest-form" className={styles.drawerForm} onSubmit={saveEditGuest}>
        <div className={styles.drawerSection}>
          <p className={styles.drawerSectionTitle}>{t("eventDashboardV2.labels.personalDetails")}</p>

          <div className={styles.drawerField}>
            <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.title")}</label>
            <CreatableSelect
              options={suffixOptions}
              value={
                guest.suffix
                  ? suffixOptions.find((o) => o.value === guest.suffix) ?? {
                      value: guest.suffix,
                      label: guest.suffix,
                    }
                  : null
              }
              onChange={(val) => handeInput("suffix", val?.value || "")}
              onCreateOption={(input) => handeInput("suffix", input.trim())}
              placeholder={t("eventDashboardV2.messages.selectOrTypeTitle")}
              formatCreateLabel={(input) => t("eventDashboardV2.messages.useTitle", { title: input })}
              styles={selectStyles}
              isClearable
            />
            {validation?.suffix && (
              <span className={styles.drawerFieldError}>{validation.suffix}</span>
            )}
          </div>

          <div className={styles.drawerFieldRow}>
            <div className={styles.drawerField}>
              <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.firstName")}</label>
              <input
                type="text"
                className={`${styles.drawerInput} ${validation?.first_name ? styles.drawerInputError : ""}`}
                value={guest.first_name || ""}
                onChange={(e) => handeInput("first_name", e.target.value)}
              />
              {validation?.first_name && (
                <span className={styles.drawerFieldError}>{validation.first_name}</span>
              )}
            </div>
            <div className={styles.drawerField}>
              <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.lastName")}</label>
              <input
                type="text"
                className={`${styles.drawerInput} ${validation?.last_name ? styles.drawerInputError : ""}`}
                value={guest.last_name || ""}
                onChange={(e) => handeInput("last_name", e.target.value)}
              />
              {validation?.last_name && (
                <span className={styles.drawerFieldError}>{validation.last_name}</span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.drawerSection}>
          <p className={styles.drawerSectionTitle}>{t("eventDashboardV2.labels.contactInformation")}</p>

          {isContactLocked ? (
            <div className={styles.drawerLockedCard}>
              <div className={styles.drawerLockedRow}>
                <span className={styles.drawerLockedLabel}>{t("eventDashboardV2.labels.phone")}</span>
                <span className={styles.drawerLockedValue}>
                  {formatPhoneDisplay(stripPipeSuffix(guest.phone || ""))}
                </span>
              </div>
              <div className={styles.drawerLockedRow}>
                <span className={styles.drawerLockedLabel}>{t("eventDashboardV2.labels.email")}</span>
                <span className={styles.drawerLockedValue}>
                  {guest.email || "—"}
                </span>
              </div>
              <div className={styles.drawerLockedFooter}>
                <LockIcon />
                <span>{t("eventDashboardV2.labels.lockedAfterInvitationSent")}</span>
              </div>
            </div>
          ) : (
            <>
              <div className={styles.drawerField}>
                <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.phoneNumber")}</label>
                <div className={`${styles.phoneInputWrap} ${validation?.phone ? styles.phoneInputError : ""}`}>
                  <PhoneInput
                    country="sa"
                    preferredCountries={GULF_COUNTRIES}
                    enableSearch
                    disableSearchIcon
                    value={guest.phone || ""}
                    onChange={handlePhoneChange}
                    inputProps={{ autoComplete: "tel" }}
                    searchPlaceholder={t("eventDashboardV2.messages.searchCountries")}
                    isValid={(value) => {
                      if (!value) return true
                      try {
                        const e164 = toE164(value)
                        const parsed = parsePhoneNumberFromString(e164)
                        if (!parsed) return true
                        return parsed.isPossible()
                      } catch {
                        return true
                      }
                    }}
                  />
                </div>
                {validation?.phone && (
                  <span className={styles.drawerFieldError}>{validation.phone}</span>
                )}
              </div>

              <div className={styles.drawerField}>
                <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.email")}</label>
                <input
                  type="text"
                  className={`${styles.drawerInput} ${validation?.email ? styles.drawerInputError : ""}`}
                  value={guest.email || ""}
                  onChange={(e) => handeInput("email", e.target.value)}
                />
                {validation?.email && (
                  <span className={styles.drawerFieldError}>{validation.email}</span>
                )}
              </div>
            </>
          )}
        </div>

        <div className={styles.drawerSection}>
          <p className={styles.drawerSectionTitle}>{t("eventDashboardV2.labels.eventDetails")}</p>

          <div className={styles.drawerField}>
            <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.zone")}</label>
            <input
              type="text"
              className={styles.drawerInput}
              value={guest.zone || ""}
              onChange={(e) => handeInput("zone", e.target.value)}
              placeholder={t("eventDashboardV2.messages.optional")}
            />
          </div>

          <div className={styles.drawerField}>
            <label className={styles.drawerFieldLabel}>{t("eventDashboardV2.labels.additionalInvites")}</label>
            <input
              type="number"
              className={`${styles.drawerInput} ${validation?.invites_quantity ? styles.drawerInputError : ""}`}
              value={guest.quantity_members ?? ""}
              onChange={(e) => handeInput("quantity_members", e.target.value)}
            />
            <span className={styles.drawerFieldHint}>{t("eventDashboardV2.messages.numberOfAdditionalGuests")}</span>
            {validation?.invites_quantity && (
              <span className={styles.drawerFieldError}>{validation.invites_quantity}</span>
            )}
          </div>
        </div>
      </form>
    </Drawer>
  )
}

export default EditGuestDrawer
